import { CompoundInterestParams, Currency, MarketScenario } from './types';

export const CURRENCY_OPTIONS: Currency[] = ['CZK', 'EUR', 'USD'];

export const CURRENCY_TO_CZK_RATES: Record<Currency, number> = {
    CZK: 1,
    EUR: 25,
    USD: 23,
};

const BASE_PARAMS: Omit<CompoundInterestParams, 'initialDeposit' | 'regularDeposit' | 'currency'> = {
    depositFrequency: 'monthly',
    interestRate: 8,
    duration: 20,
    compoundingFrequency: 1,
    inflationRate: 2.5,
    taxRate: 15,
};

export const DEFAULT_PARAMS_BY_CURRENCY: Record<Currency, CompoundInterestParams> = {
    CZK: { ...BASE_PARAMS, initialDeposit: 50000, regularDeposit: 2000, currency: 'CZK' },
    EUR: { ...BASE_PARAMS, initialDeposit: 2000, regularDeposit: 80, currency: 'EUR' },
    USD: { ...BASE_PARAMS, initialDeposit: 2000, regularDeposit: 100, currency: 'USD' },
};

export const DEFAULT_PARAMS: CompoundInterestParams = DEFAULT_PARAMS_BY_CURRENCY.CZK;

export const MARKET_SCENARIOS: MarketScenario[] = [
    {
        id: 'optimistic',
        interestRate: 10,
        inflationRate: 2.5,
        label: 'pages.compoundInterest.scenarios.optimistic',
    },
    {
        id: 'neutral',
        interestRate: 7,
        inflationRate: 3,
        label: 'pages.compoundInterest.scenarios.neutral',
    },
    {
        id: 'pessimistic',
        interestRate: 3,
        inflationRate: 4,
        label: 'pages.compoundInterest.scenarios.pessimistic',
    },
];

export const COMPOUNDING_OPTIONS = [
    { value: 1, label: 'pages.compoundInterest.calculator.annually' },
    { value: 4, label: 'pages.compoundInterest.calculator.quarterly' },
    { value: 12, label: 'pages.compoundInterest.calculator.monthlyCompounding' },
    { value: 52, label: 'pages.compoundInterest.calculator.weekly' },
    { value: 365, label: 'pages.compoundInterest.calculator.daily' },
];

export const TAX_RATES = [0, 15, 23, 35] as const;

export const CUSTOM_SCENARIO = {
    interestRate: 6,
    inflationRate: 3,
};

export const getCurrencyConversionRate = (from: Currency, to: Currency): number => {
    const fromRate = CURRENCY_TO_CZK_RATES[from] ?? 1;
    const toRate = CURRENCY_TO_CZK_RATES[to] ?? 1;

    return fromRate / toRate;
};

export const roundCurrencyAmount = (amount: number, currency: Currency): number => {
    const absoluteAmount = Math.abs(amount);

    if (currency === 'CZK') {
        return Math.round(amount);
    }

    if (absoluteAmount >= 1000) {
        return Math.round(amount / 50) * 50;
    }

    if (absoluteAmount >= 100) {
        return Math.round(amount / 10) * 10;
    }

    return Math.round(amount);
};

export const SLIDER_RANGES = {
    initialDeposit: { min: 0, max: 1000000, step: 5000 },
    regularDeposit: { min: 0, max: 50000, step: 500 },
    interestRate: { min: 0, max: 30, step: 0.5 },
    duration: { min: 1, max: 50, step: 1 },
    inflationRate: { min: 0, max: 15, step: 0.5 },
};

const CURRENCY_SLIDER_CONFIG: Record<
    Currency,
    {
        initialDeposit: { step: number; max: number };
        regularDeposit: { step: number; max: number };
    }
> = {
    CZK: {
        initialDeposit: { step: 5000, max: 1000000 },
        regularDeposit: { step: 500, max: 50000 },
    },
    EUR: {
        initialDeposit: { step: 100, max: 250000 },
        regularDeposit: { step: 10, max: 10000 },
    },
    USD: {
        initialDeposit: { step: 100, max: 250000 },
        regularDeposit: { step: 10, max: 10000 },
    },
};

export const getSliderRangesByCurrency = (currency: Currency) => {
    const currencyConfig = CURRENCY_SLIDER_CONFIG[currency] ?? CURRENCY_SLIDER_CONFIG.CZK;

    return {
        ...SLIDER_RANGES,
        initialDeposit: {
            ...SLIDER_RANGES.initialDeposit,
            step: currencyConfig.initialDeposit.step,
            max: currencyConfig.initialDeposit.max,
        },
        regularDeposit: {
            ...SLIDER_RANGES.regularDeposit,
            step: currencyConfig.regularDeposit.step,
            max: currencyConfig.regularDeposit.max,
        },
    };
};
